/**
 * O aviso e o portão de atualização.
 *
 * Um componente para os dois porque o layout é o mesmo; o que muda é se existe
 * saída. No modo OBRIGATÓRIO não há X, não há "Agora não", o Escape não fecha
 * e o toque no véu não fecha — a única coisa que a tela faz é levar à loja.
 *
 * Cada uma dessas saídas precisou ser fechada à mão. Um modal "bloqueante" que
 * some ao tocar fora não bloqueia nada; só parece que bloqueia até alguém
 * descobrir.
 *
 * O portão cobre a tela inteira, inclusive o carregamento da Início: ele fica
 * acima de tudo (z-index do overlay) e é montado no `App.jsx`, fora das rotas.
 * Por isso não existe "entrar no app enquanto o modal carrega".
 */

import React, { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { FiArrowUpCircle, FiX } from 'react-icons/fi';

import {
  getAvailableAppUpdate,
  markUpdatePromptShown,
} from '../../services/appUpdateService';
import Style from '../../styles/components/AppUpdateModal.module.css';

export default function AppUpdateModal() {
  const [update, setUpdate] = useState(null);
  const [openingStore, setOpeningStore] = useState(false);

  useEffect(() => {
    let active = true;

    const verificar = async () => {
      const availableUpdate = await getAvailableAppUpdate();
      if (!active || !availableUpdate) return;
      // O limite de 3 exibições é do AVISO. O bloqueio não tem cota: ele
      // aparece toda vez, porque a versão continua incompatível toda vez.
      if (!availableUpdate.obrigatorio) markUpdatePromptShown(availableUpdate);
      setUpdate(availableUpdate);
    };

    // O aviso espera 900 ms para não competir com a abertura do app. O
    // bloqueio não espera: meio segundo de app utilizável antes de trancar é
    // meio segundo em que dá para começar algo que não vai poder terminar.
    verificar();

    return () => {
      active = false;
    };
  }, []);

  const bloqueante = update?.obrigatorio === true;

  useEffect(() => {
    if (!update || bloqueante) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setUpdate(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [update, bloqueante]);

  if (!update) return null;

  const openStore = async () => {
    if (openingStore) return;
    setOpeningStore(true);
    try {
      await Browser.open({ url: update.storeUrl });
      // No modo bloqueante a tela NÃO se fecha ao voltar da loja: quem voltou
      // sem atualizar continua na versão velha, e o portão continua valendo.
      // Quem atualizou reabre o app num processo novo e nem vê esta tela.
      if (!bloqueante) setUpdate(null);
    } catch (error) {
      console.warn('[AppUpdate] Não foi possível abrir a loja pelo navegador nativo:', error?.message);
      window.location.assign(update.storeUrl);
    } finally {
      setOpeningStore(false);
    }
  };

  return (
    <div
      className={Style.overlay}
      role="presentation"
      onClick={bloqueante ? undefined : () => setUpdate(null)}
    >
      <section
        className={Style.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-update-title"
        onClick={(event) => event.stopPropagation()}
      >
        {!bloqueante && (
          <button
            type="button"
            className={Style.closeButton}
            onClick={() => setUpdate(null)}
            aria-label="Fechar aviso de atualização"
          >
            <FiX />
          </button>
        )}

        <div className={Style.icon} aria-hidden="true"><FiArrowUpCircle /></div>
        <h2 id="app-update-title">{update.title}</h2>
        <p>{update.message}</p>
        <span className={Style.version}>
          {bloqueante
            ? `Você está na ${update.installedVersion} · necessária ${update.latestVersion}`
            : `Versão ${update.latestVersion} disponível`}
        </span>

        <button
          type="button"
          className={Style.updateButton}
          onClick={openStore}
          disabled={openingStore}
        >
          {openingStore ? 'Abrindo loja…' : 'Atualizar'}
        </button>

        {!bloqueante && (
          <button type="button" className={Style.laterButton} onClick={() => setUpdate(null)}>
            Agora não
          </button>
        )}
      </section>
    </div>
  );
}
